import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components/brand/Logo';
import { PrimaryNavRail } from '../../components/map/PrimaryNavRail';
import { api, ApiError } from '../../lib/api';
import { dictionariesApi, type DictionaryDto } from '../../lib/dictionariesApi';
import { canManageLayers } from '../../lib/tokens';
import type { Me } from '../../lib/types';

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

export function DictionariesListPage() {
  const [me, setMe] = useState<Me | null>(null);
  const [dictionaries, setDictionaries] = useState<DictionaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    dictionariesApi
      .listDictionaries()
      .then(setDictionaries)
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить справочники.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (dict: DictionaryDto) => {
    if (!window.confirm(`Удалить справочник «${dict.name}»? Все его значения тоже будут удалены.`)) return;
    setDeletingId(dict.id);
    try {
      await dictionariesApi.deleteDictionary(dict.id);
      setDictionaries((prev) => prev.filter((d) => d.id !== dict.id));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Не удалось удалить справочник.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg-canvas">
      <PrimaryNavRail userInitials={initialsOf(me)} userAvatarUrl={me?.avatar_url} />

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="sticky top-0 z-20 flex h-[72px] shrink-0 items-center gap-3 border-b border-border-default bg-bg-surface px-8">
          <Logo variant="light" size={18} />
          <span className="text-text-secondary">/</span>
          <p className="text-sm text-text-secondary">Справочники</p>
          {canManageLayers() && (
            <div className="ml-auto flex items-center gap-3">
              <Link
                to="/app/dictionaries/add"
                className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
              >
                + Создать справочник
              </Link>
            </div>
          )}
        </header>

        <div className="px-8 pt-6">
          <h1 className="text-[28px] font-semibold text-text-primary">Справочники</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Списки значений (компании, типы объектов, статусы…), на которые может ссылаться поле любого слоя
          </p>
        </div>

        <div className="px-8 py-6">
          {loadError && <p className="mb-4 text-xs font-medium text-status-danger">{loadError}</p>}

          {loading ? (
            <p className="text-sm text-text-secondary">Загрузка…</p>
          ) : dictionaries.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border-strong bg-bg-surface p-10 text-center">
              <p className="text-[15px] font-medium text-text-primary">Справочников пока нет</p>
              <p className="mt-1 text-xs text-text-secondary">
                Создайте первый — например, справочник компаний или типов объектов
              </p>
              {canManageLayers() && (
                <Link
                  to="/app/dictionaries/add"
                  className="mt-4 inline-flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
                >
                  + Создать справочник
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border-default bg-bg-surface">
              <div className="grid grid-cols-[1fr_120px_180px_140px] gap-2 border-b border-border-default bg-bg-surface-secondary px-5 py-2.5 text-[10px] font-semibold uppercase text-text-secondary">
                <span>Название</span>
                <span>Значений</span>
                <span>Обновлён</span>
                <span />
              </div>
              {dictionaries.map((d) => (
                <div
                  key={d.id}
                  className="grid grid-cols-[1fr_120px_180px_140px] items-center gap-2 border-b border-border-default px-5 py-3 last:border-b-0 hover:bg-bg-surface-secondary"
                >
                  {canManageLayers() ? (
                    <Link to={`/app/dictionaries/${d.id}`} className="min-w-0">
                      <p className="truncate text-[13px] font-medium text-text-primary">{d.name}</p>
                      <p className="truncate text-[11px] text-text-secondary">{d.code}</p>
                    </Link>
                  ) : (
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-medium text-text-primary">{d.name}</p>
                      <p className="truncate text-[11px] text-text-secondary">{d.code}</p>
                    </div>
                  )}
                  <span className="text-xs text-text-secondary">{d.value_count.toLocaleString('ru-RU')}</span>
                  <span className="text-xs text-text-secondary">{new Date(d.updated_at).toLocaleString('ru-RU')}</span>
                  <div className="flex items-center justify-end gap-3">
                    {canManageLayers() && (
                      <Link to={`/app/dictionaries/${d.id}`} className="text-xs font-medium text-action-primary hover:underline">
                        Открыть
                      </Link>
                    )}
                    {canManageLayers() && (
                      <button
                        type="button"
                        onClick={() => handleDelete(d)}
                        disabled={deletingId === d.id}
                        className="text-xs font-medium text-status-danger hover:underline disabled:opacity-50"
                      >
                        {deletingId === d.id ? 'Удаляем…' : 'Удалить'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
